// Insights hub article content for Bharat Ethanol Forum 2026

export const ARTICLES = [
  {
    slug: "ethanol-economy-india-2030",
    pillar: true,
    title: "India's ethanol economy in 2030: the complete picture",
    dek: "The comprehensive overview — feedstock to fuel, fuel to chemicals, and India to global markets.",
    updated: "June 2026",
    readTime: "9 min read",
    image: "https://images.unsplash.com/photo-1511454493857-0a29f2c023c7?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600",
    definition:
      "India's ethanol economy is the network of feedstock, distilleries, blending infrastructure and policy that turns sugarcane, grain and crop residue into fuel-grade ethanol. By 2030 it is projected to span E30 petrol blending, flex-fuel vehicles, 2G ethanol, compressed biogas and sustainable aviation fuel.",
    sections: [
      { h: "From a blending target to an economy", p: "What began as a petrol-blending target has become an industrial economy. Ethanol blending rose from 1.5% in 2014 to 20% in 2025, hitting E20 five years early, while capacity grew from under 2 billion litres to nearly 20 billion. The question is no longer whether India can scale — it is what India builds next." },
      { h: "The four growth vectors to 2030", p: "Four vectors define the next phase: higher blends (E25, E27, E30 notified May 2026 under IS 19850:2026), flex-fuel vehicles that can run high-ethanol blends, advanced feedstocks (grain, maize, rice and 2G from crop residue), and adjacencies — compressed biogas, bio-methanol and alcohol-to-jet sustainable aviation fuel." },
      { h: "Why it matters economically", p: "Ethanol displaces crude imports, cuts the fuel import bill, lowers tailpipe CO₂ and routes value back to farmers. The programme has already channelled over ₹1.18 lakh crore to farmers through procurement, with 380+ distilleries now operating across the country." },
    ],
    table: {
      title: "India's ethanol journey",
      head: ["Milestone", "Year", "Blend / status"],
      rows: [
        ["Programme reset", "2014", "1.5% blending"],
        ["E10 achieved", "2022", "10% blending"],
        ["E20 achieved", "2025", "20% (5 years early)"],
        ["E30 standards notified", "May 2026", "IS 19850:2026"],
        ["SAF target", "2027", "1% sustainable aviation fuel"],
      ],
    },
    faqs: [
      { q: "What is India's ethanol economy?", a: "The end-to-end system that converts sugarcane, grain and biomass into fuel-grade ethanol and blends it into petrol, plus the emerging flex-fuel, 2G, CBG and SAF markets built on top of it." },
      { q: "What is India's 2030 ethanol vision?", a: "Higher blends past E20, mainstream flex-fuel vehicles, scaled 2G ethanol, and biofuel adjacencies including compressed biogas and alcohol-to-jet sustainable aviation fuel." },
    ],
  },
  {
    slug: "india-ethanol-blending-explained",
    title: "India's ethanol blending programme, explained",
    dek: "What ethanol blending is, and how India went from 1.5% to 20% in a decade.",
    updated: "June 2026",
    readTime: "6 min read",
    image: "https://images.unsplash.com/photo-1466611653911-95081537e5b7?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600",
    definition:
      "Ethanol blending is the practice of mixing fuel-grade ethanol with petrol to reduce crude oil consumption and tailpipe emissions. India's Ethanol Blended Petrol programme reached E20 (20% ethanol) in 2025, up from 1.5% in 2014.",
    sections: [
      { h: "How blending works", p: "Ethanol is splash- or in-line blended with petrol at depots. E10 means 10% ethanol, E20 means 20%. Higher blends reduce the share of imported crude in every litre and cut net CO₂ when the ethanol is made from renewable feedstock." },
      { h: "The 1.5% to 20% story", p: "Backed by guaranteed procurement, a national roadmap and rapid distillery investment, India achieved E20 in 2025 — five years ahead of its 2030 target. Production capacity grew roughly tenfold over the same period." },
    ],
    table: {
      title: "Blending by milestone",
      head: ["Year", "Blend level"],
      rows: [["2014", "1.5%"], ["2018", "~4%"], ["2022", "10% (E10)"], ["2025", "20% (E20)"]],
    },
    faqs: [
      { q: "Is E20 safe for my car?", a: "Vehicles manufactured as E20-compatible run on E20 without issue; older vehicles are generally rated for up to E10. Manufacturers specify compatible blends." },
      { q: "Does ethanol blending cut mileage?", a: "Ethanol has roughly 30% lower energy density than petrol, so very high blends can slightly reduce fuel economy — offset by lower fuel cost and emissions." },
      { q: "Which feedstocks does India use?", a: "Sugarcane juice and molasses, surplus grain, maize and rice, plus emerging 2G ethanol from crop residue." },
    ],
  },
  {
    slug: "e20-to-e30-roadmap",
    title: "From E20 to E30: India's next fuel target",
    dek: "E30 standards were notified in May 2026. Here's what comes after E20.",
    updated: "June 2026",
    readTime: "6 min read",
    image: "https://images.unsplash.com/photo-1473341304170-971dccb5ac1e?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600",
    definition:
      "E30 is petrol blended with 30% ethanol. In May 2026 India notified fuel standards for blends up to E30 (IS 19850:2026), setting the technical foundation for the post-E20 roadmap of higher blends and flex-fuel vehicles.",
    sections: [
      { h: "Why E30 now", p: "With E20 achieved early and an ethanol surplus building, higher blends absorb domestic production and deepen import substitution. The notified standards give automakers and oil companies a rulebook to plan against." },
      { h: "What it means for vehicles", p: "Moving past E20 increasingly points to flex-fuel vehicles that can run a range of ethanol blends. Existing E20-rated cars continue on E20; higher blends roll out alongside compatible fleets." },
    ],
    table: {
      title: "E20 vs E25 vs E30",
      head: ["Blend", "Ethanol %", "Status"],
      rows: [["E20", "20%", "Achieved 2025"], ["E25 / E27", "25–27%", "Transitional"], ["E30", "30%", "Standards notified May 2026"]],
    },
    faqs: [
      { q: "When does E30 roll out?", a: "Standards are notified; phased availability follows as compatible vehicles and supply scale up." },
      { q: "Do I need a new car for E30?", a: "Higher blends point toward flex-fuel vehicles; E20-rated cars remain on E20 until manufacturers certify higher blends." },
    ],
  },
  {
    slug: "what-is-flex-fuel-india",
    title: "What is flex fuel, and why India is talking about it",
    dek: "Flex-fuel vehicles can run a wide range of ethanol blends — here's why they matter.",
    updated: "June 2026",
    readTime: "5 min read",
    image: "https://images.unsplash.com/photo-1492144534655-ae79c964c9d7?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600",
    definition:
      "A flex-fuel vehicle (FFV) is designed to run on petrol blended with a wide range of ethanol — from regular blends up to E85 — using a single engine that adapts automatically. They let India absorb its growing ethanol surplus.",
    sections: [
      { h: "The surplus story", p: "India now produces more ethanol than its current vehicle fleet can absorb at E20. Flex-fuel vehicles unlock demand for higher blends, turning surplus into displaced crude." },
      { h: "What changes for buyers", p: "FFVs use modified fuel systems and engine calibration. Policy incentives and GST treatment shape adoption, alongside the spread of higher-blend fuel at pumps." },
    ],
    table: {
      title: "Blend compatibility",
      head: ["Vehicle type", "Runs on"],
      rows: [["Standard (older)", "Up to E10"], ["E20-compliant", "Up to E20"], ["Flex-fuel (FFV)", "Up to E85"]],
    },
    faqs: [
      { q: "Which cars are flex-fuel in India?", a: "Several automakers are developing FFV models as higher-blend infrastructure scales; availability is expanding." },
      { q: "What is E85?", a: "Petrol blended with up to 85% ethanol, usable only by flex-fuel vehicles." },
    ],
  },
  {
    slug: "saf-from-ethanol-india",
    title: "Sustainable aviation fuel from ethanol: India's alcohol-to-jet opportunity",
    dek: "India's 1%-by-2027 SAF target and the alcohol-to-jet pathway off the ethanol surplus.",
    updated: "June 2026",
    readTime: "6 min read",
    image: "https://images.unsplash.com/photo-1436491865332-7a61a109cc05?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600",
    definition:
      "Sustainable aviation fuel (SAF) is jet fuel made from renewable feedstock instead of fossil crude. India's alcohol-to-jet (ATJ) pathway converts surplus ethanol into SAF, supporting a 1% SAF target by 2027.",
    sections: [
      { h: "Why ethanol to jet", p: "With an ethanol surplus building, alcohol-to-jet offers a high-value outlet that decarbonises aviation — a sector with few alternatives to liquid fuel." },
      { h: "The mandate timeline", p: "India has signalled a 1% SAF blend target by 2027, rising over time. Ethanol-derived SAF positions domestic producers in a fast-growing global market." },
    ],
    table: {
      title: "SAF at a glance",
      head: ["Question", "Answer"],
      rows: [["Feedstock", "Surplus ethanol (ATJ)"], ["Target", "1% by 2027"], ["Sector", "Aviation decarbonisation"]],
    },
    faqs: [
      { q: "What is SAF?", a: "Jet fuel produced from renewable sources such as ethanol, reducing lifecycle aviation emissions." },
      { q: "What is India's SAF mandate?", a: "A signalled 1% SAF target by 2027, with the alcohol-to-jet pathway as a key route." },
    ],
  },
  {
    slug: "2g-ethanol-explained",
    title: "2G ethanol: turning crop residue into fuel",
    dek: "Second-generation ethanol made from biomass — how it differs from 1G and why it matters.",
    updated: "June 2026",
    readTime: "5 min read",
    image: "https://images.unsplash.com/photo-1500382017468-9049fed747ef?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600",
    definition:
      "2G (second-generation) ethanol is made from non-food biomass such as crop residue, rather than sugar or grain. It expands feedstock beyond food crops and helps tackle stubble burning while adding to India's ethanol supply.",
    sections: [
      { h: "1G vs 2G", p: "First-generation ethanol uses sugar and starch (cane, grain). 2G uses cellulosic biomass — straw, husk and residue — through advanced conversion, widening the feedstock base without competing with food." },
      { h: "Why it matters", p: "2G turns agricultural waste into value, reduces open-field stubble burning, and supports surplus and rural income — a strategic complement to 1G as blends rise." },
    ],
    table: {
      title: "1G vs 2G ethanol",
      head: ["Type", "Feedstock", "Note"],
      rows: [["1G", "Sugar, grain", "Mature, scaled"], ["2G", "Crop residue / biomass", "Emerging, waste-to-fuel"]],
    },
    faqs: [
      { q: "What is 2G ethanol?", a: "Ethanol made from non-food cellulosic biomass like crop residue, rather than sugar or grain." },
      { q: "Why is 2G important for India?", a: "It widens feedstock, monetises agricultural waste and helps reduce stubble burning." },
    ],
  },
];

export const getArticle = (slug) => ARTICLES.find((a) => a.slug === slug);
